import { generateJSON } from '@tiptap/html';
import { renderToHTMLString } from '@tiptap/static-renderer/pm/html-string';
import { z } from 'zod/v4-mini';
import { getRichTextExtensions } from './rich-text-extensions';
// serializer renders any stored content — always the full schema (see rich-text-extensions)
const extensions = getRichTextExtensions('full');
function isProseMirrorDoc(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.type === 'doc' &&
        Array.isArray(value.content));
}
export function hasTextContent(node) {
    if (typeof node.text === 'string' && node.text.trim().length > 0)
        return true;
    return (node.content ?? []).some(hasTextContent);
}
/**
 * Tiptap adds a trailing empty paragraph to the end of the document.
 * Strips trailing empty paragraphs from a ProseMirror document.
 * @param doc
 * @returns
 */
function stripTrailingEmptyParagraphs(doc) {
    if (!doc.content)
        return doc;
    const content = [...doc.content];
    while (content.length > 0) {
        const last = content[content.length - 1];
        if (last.type === 'paragraph' &&
            (!last.content || last.content.length === 0)) {
            content.pop();
        }
        else {
            break;
        }
    }
    return { ...doc, content };
}
export function htmlToPlainText(html) {
    return html
        .replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
}
export function escapeHtmlCharacters(text) {
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return escaped;
}
export function richTextToHtml(doc) {
    if (!doc || !isProseMirrorDoc(doc))
        return '';
    const stripped = stripTrailingEmptyParagraphs(doc);
    if (!stripped.content || stripped.content.length === 0)
        return '';
    return (renderToHTMLString({ content: stripped, extensions })
        // Remove empty attributes that are rendered as `attr=null` in the HTML string
        .replace(/\s+\w+="null"/g, ''));
}
/**
 * Narrows persisted JSON to a valid ProseMirror doc for use as editor `content`.
 * Unconditional HTML round-trip (~ms, once on mount) — see inline note.
 */
export function normalizeEditorContent(content, editorExtensions = extensions) {
    if (content == null ||
        typeof content !== 'object' ||
        content.type !== 'doc' ||
        !Array.isArray(content.content)) {
        return undefined;
    }
    // Loading JSON with a node type outside the schema makes ProseMirror discard the
    // WHOLE doc. Re-parsing via HTML is lenient instead: unknown nodes flatten to
    // paragraphs (text + allowed marks kept). Render with full schema (preserve all),
    // parse with the editor's (drop what it can't hold). Lossless when already valid.
    const html = renderToHTMLString({ content, extensions });
    return generateJSON(html, editorExtensions);
}
/**
 * Creates a Zod schema that validates a ProseMirror `JSONContent` value has text content.
 *
 * @param message - Validation error message shown when the field is empty.
 * Defaults to `'This field is required'`.
 */
export function createRichTextSchema(message = 'This field is required') {
    return z
        .custom(() => true)
        .check(z.refine((val) => {
        if (val == null || typeof val !== 'object')
            return false;
        return hasTextContent(val);
    }, message));
}
export const EmptyRichTextDoc = { type: 'doc', content: [] };
const SAFE_LINK_SCHEMES = /^(https?|mailto|tel):/i;
/**
 * Strips link marks with unsafe URL schemes (e.g. javascript:) from a ProseMirror doc.
 * Call on submit before persisting — not on every keystroke.
 */
export function sanitizeRichTextDoc(doc) {
    if (!doc.content)
        return doc;
    return { ...doc, content: doc.content.map(sanitizeNode) };
}
function sanitizeNode(node) {
    const marks = node.marks?.filter((mark) => {
        if (mark.type !== 'link')
            return true;
        const href = mark.attrs?.href;
        if (typeof href !== 'string')
            return false;
        return SAFE_LINK_SCHEMES.test(href);
    });
    const content = node.content?.map(sanitizeNode);
    return {
        ...node,
        ...(marks !== undefined && { marks }),
        ...(content !== undefined && { content }),
    };
}
/**
 * Resolves a ProseMirror document from a JSON blob or HTML string fallback.
 *
 * Priority order:
 * 1. If `json` is a valid ProseMirror doc with text content, return it as-is.
 * 2. Otherwise, if `html` is provided, parse it into a ProseMirror doc and return it if it has text content.
 * 3. Fall back to an empty doc (`{ type: 'doc', content: [] }`).
 */
export function resolveRichTextDoc(json, html) {
    if (isProseMirrorDoc(json) && hasTextContent(json)) {
        return json;
    }
    if (html) {
        try {
            const parsed = generateJSON(html, extensions);
            if (isProseMirrorDoc(parsed) && hasTextContent(parsed)) {
                return parsed;
            }
        }
        catch {
            // fall through to empty doc
        }
    }
    return EmptyRichTextDoc;
}
